<template>
    <div>
        <transition name="modal">
            <div class="modal-mask task-modal-mask">
                <div class="modal-wrapper">
                    <div class="modal-container">
                        <app-sub-navigation-bar
                            :segments="breadcrumbSegments"
                            :view-mode="catalogViewMode"
                            :is-at-root="isAtCategoryTreeRoot"
                            :task-code="taskBreadcrumbCode"
                            @navigate-all="$emit('breadcrumb-navigate-all')"
                            @navigate-segment="$emit('breadcrumb-navigate-segment', $event)"
                        />

                        <div class="task-modal-shell">
                            <div class="task-modal-body">
                                <article class="task-modal-selected-product addon-task-added">
                                    <div class="task-modal-selected-product__image">
                                        <img
                                            v-if="taskImageUrl"
                                            :src="taskImageUrl"
                                            :alt="task.title || ''"
                                        />
                                        <img
                                            v-else
                                            src="../../../../assets/img/no-image-available.jpg"
                                            alt=""
                                        />
                                    </div>
                                    <div class="task-modal-selected-product__body addon-task-added__body">
                                        <div class="addon-task-added__content">
                                            <div class="addon-task-added__message">
                                                <i class="check circle outline icon" aria-hidden="true"></i>
                                                <span>Added to quote</span>
                                            </div>
                                            <h5 class="addon-task-added__title">{{ task.title }}</h5>
                                        </div>
                                        <button
                                            type="button"
                                            class="ui primary button addon-task-added__back"
                                            @click="$emit('close')"
                                        >Back to all tasks</button>
                                    </div>
                                </article>

                                <hr class="task-modal-divider" />

                                <div class="addon-task-list-header">
                                    <h5 class="task-modal-field-label">You might also need:</h5>
                                </div>

                                <div class="quote-task-grid" v-if="addonList.length">
                                    <app-quote-task-tile
                                        v-for="addon in addonList"
                                        :key="addon.id || addon.uuid || addon.title"
                                        :code="addon.code"
                                        :title="addon.title || addon.name"
                                        :image-src="getImageUrl(addon)"
                                        add-aria-label="Add to quote"
                                        @add="$emit('add', addon)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>
<style scoped>
    .addon-task-added {
        max-width: none;
        margin-top: 32px;
        padding: 16px;
        align-items: center;
        background: #EFF4F6;
        border: 0;
    }

    .addon-task-added__body {
        flex-direction: row;
        align-items: center;
        gap: 16px;
        border-left: 0;
        padding-left: 0;
    }

    .addon-task-added__content {
        flex: 1;
        min-width: 0;
    }

    .addon-task-added .task-modal-selected-product__image {
        width: 64px;
        height: 64px;
    }

    .addon-task-added__message {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin: 0 0 8px;
        padding: 0;
        color: #4DA1FF;
        font-family: var(--font-regular);
        font-size: 14px;
        line-height: 17px;
    }

    .addon-task-added__message .icon {
        color: #4DA1FF;
        margin: 0 !important;
    }

    .addon-task-added__title {
        margin-top: 0;
        margin-bottom: 0;
        font-family: var(--font-semibold) !important;
        font-size: 18px;
        font-weight: 600;
        letter-spacing: 0.5px;
        color: #323C47;
    }

    .addon-task-added__back {
        flex: 0 0 180px;
        align-self: center;
        margin: 0;
    }

    .addon-task-list-header {
        margin-top: 0;
    }

    /* Match catalog spacing inside the task modal shell */
    .quote-task-grid {
        padding-bottom: 24px;
    }

    @media screen and (max-width: 767px) {
        .addon-task-added {
            margin-top: 16px;
            padding: 16px;
            gap: 12px;
            align-items: flex-start;
        }

        .addon-task-added__body {
            flex-direction: column;
            align-items: stretch;
            gap: 16px;
        }

        .addon-task-added__content {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .addon-task-added__message,
        .addon-task-added__title {
            margin: 0;
        }

        .addon-task-added__back {
            flex-basis: auto;
            align-self: flex-start;
            width: auto;
            min-height: 44px;
        }
    }

    @media screen and (max-width: 620px) {
        .addon-task-added .task-modal-selected-product__image {
            display: none;
        }
    }

</style>
<script>
  import SubNavigationBar from '../../../common/SubNavigationBar.vue';
  import QuoteTaskTile from '../../../common/QuoteTaskTile.vue';

  export default {
    components: {
        'app-sub-navigation-bar': SubNavigationBar,
        'app-quote-task-tile': QuoteTaskTile,
    },
    computed: {
        addonList: function () {
            const defaults = this.addons && Array.isArray(this.addons.default) ? this.addons.default : [];
            const company = this.addons && Array.isArray(this.addons.company) ? this.addons.company : [];
            return defaults.concat(company);
        },
        taskBreadcrumbCode: function () {
            const task = this.task || {};
            if (task.code != null && String(task.code).trim() !== '') {
                return String(task.code).trim();
            }
            if (task.uuid != null && String(task.uuid).trim() !== '') {
                return String(task.uuid).trim();
            }
            return '';
        },
        taskImageUrl: function () {
            return this.getImageUrl(this.task);
        },
    },
    mounted: function () {
        document.body.classList.add('no-scroll');
    },
    beforeUnmount: function () {
        document.body.classList.remove('no-scroll');
    },
    props: {
        task: {
            type: Object,
        },
        addons: {
            type: Object,
            default: () => ({ company: [], default: [] }),
        },
        breadcrumbSegments: {
            type: Array,
            default: function () { return [] }
        },
        catalogViewMode: {
            type: String,
            default: 'browse'
        },
        isAtCategoryTreeRoot: {
            type: Boolean,
            default: true
        }
    }
  }
</script>
